import React from 'react';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Spinner from 'react-bootstrap/Spinner';
import Badge from 'react-bootstrap/Badge';
import Alert from 'react-bootstrap/Alert';
import { TrashFill } from 'react-bootstrap-icons';
import {
  EXPIRY_CATEGORIES,
  PRODUCT_CATEGORIES,
  PRODUCT_MEASUREMENT_UNITS,
  PRODUCT_STORAGE_OPTIONS,
} from '../../config_and_helpers/config';
import NumberInput from '../Generic/NumberInput';
import DropdownSelect from '../Generic/DropdownSelect';
import {
  ENRICHMENT_IMAGE_ACCEPT,
  formatFieldDisplayValue,
  getFieldLabel,
} from '../../utils/productEnrichment';

const FIELD_LABELS = {
  name: 'Product name',
  manufacturer: 'Manufacturer (OEM brand)',
  category: 'Category',
  unit: 'Measurement unit',
  unit_quantity: 'Unit quantity',
  units_per_sub_unit: 'Units per sub-unit',
  storage: 'Storage',
  notes: 'Notes',
  url: 'Product URL',
  expiry_relevant: 'Expiry relevant',
  kit_description: 'Kit description',
  image_url: 'Image URL',
};

/**
 * Current-value hint and optional writable badge under each editable field.
 */
const FieldCurrentHint = ({ fieldKey, fieldDiffs }) => {
  const diff = fieldDiffs?.[fieldKey];
  const writable = diff?.writable === true;
  return (
    <div className="d-flex flex-wrap align-items-center gap-2">
      <Form.Text className="text-muted mb-0">
        Current: {formatFieldDisplayValue(diff?.current)}
      </Form.Text>
      {writable && (
        <Badge bg="secondary" className="fw-normal">
          Fills if empty
        </Badge>
      )}
    </div>
  );
};

/**
 * Editable enrichment review form (pre-filled from suggested_form / product_snapshot).
 */
const ProductEnrichmentEditForm = ({
  formValues,
  onChange,
  fieldDiffs = {},
  manufacturerList = [],
  loadingManufacturers = false,
  hasExistingImage = false,
  pendingImagePreviewUrl = null,
  imageUploading = false,
  imageUploadError = null,
  productImageId = null,
  onImageFileSelected = () => {},
  onClearPendingImage = () => {},
}) => {
  const handleChange = fieldKey => event => {
    onChange(fieldKey, event.target.value);
  };

  const renderSelect = (fieldKey, options, emptyLabel = 'Select…') => (
    <Form.Group className="mb-3" controlId={`enrichment-${fieldKey}`}>
      <Form.Label>{FIELD_LABELS[fieldKey] || getFieldLabel(fieldKey)}</Form.Label>
      <Form.Select
        value={formValues[fieldKey] ?? ''}
        onChange={handleChange(fieldKey)}
      >
        <option value="">{emptyLabel}</option>
        {options.map(opt => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </Form.Select>
      <FieldCurrentHint fieldKey={fieldKey} fieldDiffs={fieldDiffs} />
    </Form.Group>
  );

  const selectedManufacturer =
    manufacturerList.find(
      m => String(m.value) === String(formValues.manufacturer ?? '')
    ) || null;

  const hasPendingUpload = Boolean(pendingImagePreviewUrl);
  const urlPreview =
    formValues.image_url && !hasPendingUpload && !hasExistingImage;
  const imageInputsDisabled = hasExistingImage || imageUploading;

  return (
    <Form>
      <Row>
        <Col md={6}>
          <Form.Group className="mb-3" controlId="enrichment-name">
            <Form.Label>{FIELD_LABELS.name}</Form.Label>
            <Form.Control
              type="text"
              value={formValues.name ?? ''}
              onChange={handleChange('name')}
            />
            <FieldCurrentHint fieldKey="name" fieldDiffs={fieldDiffs} />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3" controlId="enrichment-manufacturer">
            <Form.Label>{FIELD_LABELS.manufacturer}</Form.Label>
            <DropdownSelect
              optionsList={manufacturerList}
              placeholder={
                loadingManufacturers ? 'Loading manufacturers…' : 'Related Manufacturer'
              }
              selectedValue={selectedManufacturer}
              setSelectedValue={option =>
                onChange('manufacturer', option?.value ?? '')
              }
              touched={false}
              error={null}
              isInvalid={false}
              onBlur={() => {}}
              disabledOptions={[]}
              disabled={loadingManufacturers}
            />
            {loadingManufacturers && (
              <Spinner size="sm" className="ms-2 align-middle" />
            )}
            <FieldCurrentHint fieldKey="manufacturer" fieldDiffs={fieldDiffs} />
          </Form.Group>
        </Col>
      </Row>

      <Row>
        <Col md={6}>{renderSelect('category', PRODUCT_CATEGORIES)}</Col>
        <Col md={6}>{renderSelect('unit', PRODUCT_MEASUREMENT_UNITS)}</Col>
      </Row>

      <Row>
        <Col md={6}>
          <Form.Group className="mb-3" controlId="enrichment-unit_quantity">
            <Form.Label>{FIELD_LABELS.unit_quantity}</Form.Label>
            <NumberInput
              name="unit_quantity"
              value={formValues.unit_quantity ?? ''}
              onChange={handleChange('unit_quantity')}
            />
            <FieldCurrentHint fieldKey="unit_quantity" fieldDiffs={fieldDiffs} />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3" controlId="enrichment-units_per_sub_unit">
            <Form.Label>{FIELD_LABELS.units_per_sub_unit}</Form.Label>
            <NumberInput
              name="units_per_sub_unit"
              value={formValues.units_per_sub_unit ?? ''}
              onChange={handleChange('units_per_sub_unit')}
            />
            <FieldCurrentHint
              fieldKey="units_per_sub_unit"
              fieldDiffs={fieldDiffs}
            />
          </Form.Group>
        </Col>
      </Row>

      <Row>
        <Col md={6}>{renderSelect('storage', PRODUCT_STORAGE_OPTIONS)}</Col>
        <Col md={6}>{renderSelect('expiry_relevant', EXPIRY_CATEGORIES)}</Col>
      </Row>

      <Form.Group className="mb-3" controlId="enrichment-url">
        <Form.Label>{FIELD_LABELS.url}</Form.Label>
        <Form.Control
          type="url"
          value={formValues.url ?? ''}
          onChange={handleChange('url')}
          placeholder="https://…"
        />
        <FieldCurrentHint fieldKey="url" fieldDiffs={fieldDiffs} />
      </Form.Group>

      <Form.Group className="mb-3" controlId="enrichment-notes">
        <Form.Label>{FIELD_LABELS.notes}</Form.Label>
        <Form.Control
          as="textarea"
          rows={2}
          value={formValues.notes ?? ''}
          onChange={handleChange('notes')}
        />
        <FieldCurrentHint fieldKey="notes" fieldDiffs={fieldDiffs} />
      </Form.Group>

      {formValues.category === 'Kit' && (
        <Form.Group className="mb-3" controlId="enrichment-kit_description">
          <Form.Label>{FIELD_LABELS.kit_description}</Form.Label>
          <Form.Control
            as="textarea"
            rows={2}
            value={formValues.kit_description ?? ''}
            onChange={handleChange('kit_description')}
          />
          <FieldCurrentHint fieldKey="kit_description" fieldDiffs={fieldDiffs} />
        </Form.Group>
      )}

      <Form.Group className="mb-0" controlId="enrichment-image">
        <Form.Label>Product image</Form.Label>
        {hasExistingImage ? (
          <Form.Text className="d-block text-muted mb-2">
            Product already has an image. Image cannot be added via enrichment.
          </Form.Text>
        ) : (
          <Form.Text className="d-block text-muted mb-2">
            Paste a public image URL (server fetch) or upload a file. Uploaded
            file takes precedence on submit.
          </Form.Text>
        )}

        {!hasExistingImage && (
          <>
            <Form.Group className="mb-2" controlId="enrichment-image_url">
              <Form.Label className="small text-muted mb-1">Image URL</Form.Label>
              <Form.Control
                type="url"
                size="sm"
                value={formValues.image_url ?? ''}
                onChange={handleChange('image_url')}
                placeholder="https://…"
                disabled={imageInputsDisabled || hasPendingUpload}
              />
            </Form.Group>

            <Form.Group className="mb-2" controlId="enrichment-image_file">
              <Form.Label className="small text-muted mb-1">Upload file</Form.Label>
              <Form.Control
                type="file"
                size="sm"
                accept={ENRICHMENT_IMAGE_ACCEPT}
                disabled={imageInputsDisabled}
                onChange={event => {
                  const file = event.target.files?.[0];
                  if (file) {
                    onImageFileSelected(file);
                    event.target.value = '';
                  }
                }}
              />
            </Form.Group>

            {imageUploading && (
              <div className="d-flex align-items-center gap-2 text-muted small mb-2">
                <Spinner animation="border" size="sm" />
                Uploading image…
              </div>
            )}

            {imageUploadError && (
              <Alert variant="danger" className="small py-2 mb-2">
                {imageUploadError}
              </Alert>
            )}

            {productImageId != null && hasPendingUpload && !imageUploading && (
              <Form.Text className="text-success d-block mb-2">
                Image ready (id {productImageId}) — will be linked on Submit.
              </Form.Text>
            )}
          </>
        )}

        <FieldCurrentHint fieldKey="image_url" fieldDiffs={fieldDiffs} />

        {hasPendingUpload && (
          <div className="mt-2 d-flex align-items-start gap-2">
            <a
              href={pendingImagePreviewUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                src={pendingImagePreviewUrl}
                alt="Upload preview"
                style={{ maxWidth: 160, maxHeight: 100, objectFit: 'contain' }}
              />
            </a>
            {!imageUploading && (
              <TrashFill
                size={18}
                color="red"
                role="button"
                aria-label="Remove uploaded image"
                style={{ cursor: 'pointer', marginTop: 4 }}
                onClick={onClearPendingImage}
              />
            )}
          </div>
        )}

        {urlPreview ? (
          <div className="mt-2">
            <img
              src={formValues.image_url}
              alt="Suggested product"
              style={{ maxWidth: 160, maxHeight: 100, objectFit: 'contain' }}
              onError={e => {
                e.target.style.display = 'none';
              }}
            />
          </div>
        ) : null}
      </Form.Group>
    </Form>
  );
};

export default ProductEnrichmentEditForm;
